import { useState } from "react"
import { DateRangePicker } from "rsuite"
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Paper,
  Button,
} from "@mui/material"
import { useCustomQuery } from "@/hooks/useCustomQuery"
import Chart from "@/components/charts/Chart"
import DataTable from "@/components/Tables/DataTable"
import { columns } from "./columns"
import "rsuite/dist/rsuite.min.css"
import { exportReport } from "@/hooks/useReportExport"
import { formatToDevanagariStyle } from "@/utilities/helper"
import { BrandSelect } from "../../Brand/BrandSelect"
import { useInitBrands } from "@/hooks/useInitBrands"
import { useAppSelector } from "@/lib/hooks"
import SalesOrderTypeSelect from "../SalesOrderTypeSelect"
import { useReportBranch } from "@/components/Reports/ReportBranchFilter"

const DailyReport = ({ orderType, onOrderTypeChange }) => {
  const [dateRange, setDateRange] = useState([
    new Date(new Date().setDate(new Date().getDate() - 7)),
    new Date(),
  ])
  const selectedBrand = useAppSelector((state) => state.brand.selectedBrandId)
  const { branchId } = useReportBranch()

  const { data: salesData, isLoading } = useCustomQuery(
    [
      "daily-sales-report",
      dateRange[0],
      dateRange[1],
      selectedBrand,
      orderType,
      branchId,
    ],
    `report/sales/daily?start_date=${dateRange[0].toLocaleDateString("en-CA")}&end_date=${dateRange[1].toLocaleDateString("en-CA")}&brand_id=${selectedBrand}&order_type=${orderType}&branch_id=${branchId ?? ""}`
  )
  useInitBrands()

  const data = salesData?.daily_sales
  const reversedData = data ? [...data].reverse() : []
  const payment_totals = salesData?.payment_totals

  // Export report
  const handleExport = async () => {
    return exportReport({
      startDate: dateRange[0].toLocaleDateString("en-CA"),
      endDate: dateRange[1].toLocaleDateString("en-CA"),
      brandId: selectedBrand,
      orderType,
      branchId,
      reportType: "daily-sales",
    })
  }

  // Summary statistics
  const summaryStats = data
    ? {
        grossSales: data.reduce(
          (sum, item) => sum + item.total_sales + (item.sales_return || 0),
          0
        ),
        netSales: data.reduce((sum, item) => sum + item.total_sales, 0),
        totalOrders: data.reduce((sum, item) => sum + item.total_orders, 0),
        averageSale:
          data.reduce((sum, item) => sum + item.total_sales, 0) / data.length,
        totalSalesReturn: data.reduce(
          (sum, item) => sum + (item.sales_return || 0),
          0
        ),
        totalExpenses: data.reduce(
          (sum, item) => sum + (item.expenses || 0),
          0
        ),
        dailyExpenses: data.reduce(
          (sum, item) => sum + (item.daily_expenses || 0),
          0
        ),
        overallExpenses: data.reduce(
          (sum, item) => sum + (item.overall_expenses || 0),
          0
        ),
      }
    : null

  // Prepare chart data
  const chartData = {
    labels: [],
    salesData: [],
    ordersData: [],
  }

  if (data) {
    data.forEach((item) => {
      chartData.labels.push(item.sale_date)
      chartData.salesData.push(item.total_sales)
      chartData.ordersData.push(item.total_orders)
    })
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Daily Sales Report
      </Typography>

      {/* Date Picker */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Select Date Range
          </Typography>
          <div className="flex flex-wrap items-center gap-2">
            <DateRangePicker
              className="flex-1"
              value={dateRange}
              onChange={setDateRange}
              format="MMM dd, yyyy"
              placeholder="Select date range"
              cleanable={false}
              style={{ width: 300 }}
              appearance="default"
            />
            <SalesOrderTypeSelect
              value={orderType}
              onChange={onOrderTypeChange}
            />
            <div className="flex-1">
              <BrandSelect />
            </div>
            <Button
              variant="outlined"
              onClick={handleExport}
              sx={{
                textTransform: "capitalize",
                borderRadius: "7px",
                fontWeight: "500",
                fontSize: "13px",
                padding: "6px 3px",
              }}
              color="primary"
            >
              Export
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Summary Cards */}
      {summaryStats && (
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} md={4}>
            <Paper elevation={3} sx={{ p: 2, textAlign: "center" }}>
              <Typography variant="subtitle1">Net Sales</Typography>
              <Typography variant="h5" color="primary">
                Rs.{" "}
                {summaryStats.netSales.toLocaleString("en-IN", {
                  maximumFractionDigits: 2,
                })}
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} md={4}>
            <Paper elevation={3} sx={{ p: 2, textAlign: "center" }}>
              <Typography variant="subtitle1">Total Orders</Typography>
              <Typography variant="h5" color="secondary">
                {summaryStats.totalOrders.toLocaleString()}
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} md={4}>
            <Paper elevation={3} sx={{ p: 2, textAlign: "center" }}>
              <Typography variant="subtitle1">Average Daily Sale</Typography>
              <Typography variant="h5">
                Rs.{" "}
                {summaryStats.averageSale.toLocaleString("en-IN", {
                  maximumFractionDigits: 2,
                })}
              </Typography>
            </Paper>
          </Grid>
        </Grid>
      )}

      {/* Charts */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Daily Sales
              </Typography>
              <Chart
                type="line"
                data={chartData.salesData}
                labels={chartData.labels}
                title="Daily Sales"
                backgroundColor="rgba(63, 81, 181, 0.5)"
                borderColor="#3f51b5"
              />
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Daily Orders
              </Typography>
              <Chart
                type="bar"
                data={chartData.ordersData}
                labels={chartData.labels}
                title="Daily Orders"
                backgroundColor="rgba(76, 175, 80, 0.5)"
                borderColor="#4caf50"
              />
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Payment Totals */}
      <Card className="mb-2">
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: "bold" }} gutterBottom>
            Payment Summary
          </Typography>

          {payment_totals?.map((method) => (
            <div key={method.payment_method}>
              <span className="font-semibold capitalize">
                {method.payment_method}:
              </span>{" "}
              Rs. {formatToDevanagariStyle(method.total_sales)}
            </div>
          ))}

          {/* Gross Sales */}
          <div className="mt-2">
            <span className="font-semibold capitalize">Gross Sales:</span> Rs.{" "}
            {formatToDevanagariStyle(summaryStats?.grossSales || 0)}
          </div>

          {/* Sales Return */}
          {summaryStats?.totalSalesReturn > 0 && (
            <div className="mt-1">
              <span className="font-semibold capitalize">Sales Return:</span>{" "}
              Rs. {formatToDevanagariStyle(summaryStats.totalSalesReturn)}
            </div>
          )}

          <div className="mt-1">
            <span className="font-semibold">Expenses:</span> Rs.{" "}
            {formatToDevanagariStyle(summaryStats?.totalExpenses || 0)}
            <span className="text-sm text-gray-500">
              {" "}
              (Daily: Rs.{" "}
              {formatToDevanagariStyle(summaryStats?.dailyExpenses || 0)},
              Overall: Rs.{" "}
              {formatToDevanagariStyle(summaryStats?.overallExpenses || 0)})
            </span>
          </div>

          {/* Net Sales */}
          <div className="mt-1">
            <span className="font-semibold capitalize">Net Sales:</span> Rs.{" "}
            {formatToDevanagariStyle(summaryStats?.netSales || 0)}
          </div>
          <Typography variant="caption" color="text.secondary">
            Expenses include all brands and order types, and are shown
            separately from Net Sales.
          </Typography>
        </CardContent>
      </Card>

      {/* Data Table */}
      <Card>
        <DataTable
          label="Detailed Sales Data"
          columns={columns}
          data={reversedData}
          isLoading={isLoading}
        />
      </Card>
    </Box>
  )
}

export default DailyReport
